import * as preact from "preact";

import { effect, untracked } from "@preact/signals";

import * as audioPlayer from "audioPlayer";
import * as keyboardShortcuts from "keyboardShortcuts";
import * as modalController from "modalController";
import type { ContextSentenceNode } from "readerText/tokens";
import { hasSpeakableText } from "readerText/tokens";
import ReaderViewStore from "stores/ReaderViewStore";
import * as tts from "tts";
import type { ReaderFooterState } from "views/ReaderFooterView";
import TTSVoicePickerView from "views/TTSVoicePickerView";
import * as walkthrough from "walkthrough";
import * as browserTTSVoices from "../browserTTSVoices";

interface ReadAloudControllerDeps {
  stores: any;
  readerViewStore: ReaderViewStore;
  getSentenceFromPosition: (position: number) => ContextSentenceNode[];
  options: any;
  setupFormattingOptions: () => Promise<void>;
  updateFooterState: (footerState: ReaderFooterState) => void;
  clearAudioPositionHighlight: () => void;
  highlightAudioPosition: (position: number) => void;
  onReadAloudPlaybackChange?: (isPlaying: boolean) => void;
  onGotoNextPage: (keepAudioPlaying: boolean) => void;
  onGotoPreviousPage: (keepAudioPlaying: boolean) => void;
}

export function createReadAloudController(deps: ReadAloudControllerDeps) {
  const { stores, readerViewStore, options } = deps;

  let ttsPosition: number | undefined = undefined;
  let ttsVoiceSelected = false;
  let readAloudButton: any;
  let readAloudPlaying = false;
  let readAloudBuffering = false;
  let readAloudTimeout: any;
  let skipAudioButtonsShowing = false;
  // Set while Read Aloud has triggered a page turn but the new page hasn't
  // rendered yet. Boundary callbacks from the still-playing sentence should
  // not trigger duplicate page turns during this window.
  let pendingPageTurn = false;
  // Set when the current sentence ends before the pending page turn has
  // rendered. Once the new page is ready, playback resumes from this specific
  // next-sentence position instead of the top of the page.
  let pendingResumePosition: number | undefined;
  // Tracks a deferred resume scheduled by jumpReadAloud so rapid skip-forward
  // / skip-backward clicks coalesce into a single new playback. Without this,
  // each click would queue another playReadAloud and several in-flight
  // tts.play()s would trample each other's highlight callbacks.
  let pendingSkipTimeout: any;
  // Ignore late boundary/end callbacks from a previous sentence after a page
  // turn or restart has begun.
  let ttsPlaybackRunID = 0;
  const SKIP_DEBOUNCE_MS = 250;
  // Disposes the availability-recovery effect (see watchForReadAloudRecovery).
  let disposeAvailabilityRecovery: (() => void) | undefined;

  const updateReadAloudFooterState = () => {
    deps.updateFooterState({
      readAloudAvailable: Boolean(readAloudButton),
      readAloudBuffering,
      readAloudPlaying,
      readAloudSkipButtonsVisible: skipAudioButtonsShowing,
    });
  };

  /**
   * Read Aloud's controls on the lock screen and in Control Center (the
   * Media Session API), as for other things playing
   */
  const updateMediaSession = (isPlaying: boolean) => {
    const { mediaSession } = navigator;
    if (!mediaSession) return;
    mediaSession.playbackState = isPlaying ? "playing" : "paused";
    if (!isPlaying) return;
    mediaSession.metadata = new MediaMetadata({
      title: stores.bookModel.book.value.title,
      artist: "Readlang",
      artwork: [{ src: "/icon.png", sizes: "196x196", type: "image/png" }],
    });
    mediaSession.setActionHandler("play", playReadAloud);
    mediaSession.setActionHandler("pause", () => pauseReadAloud());
    mediaSession.setActionHandler("nexttrack", jumpForward);
    mediaSession.setActionHandler("previoustrack", jumpBackward);
    mediaSession.setActionHandler("seekforward", jumpForward);
    mediaSession.setActionHandler("seekbackward", jumpBackward);
    // Each sentence is a clip of its own, which would show as all there is to
    // play: an empty timeline instead (a text's length is only a guess)
    if (stores.bookModel.book.value.audioFileID == null) {
      mediaSession.setPositionState({
        duration: 0,
        playbackRate: 1,
        position: 0,
      });
    }
  };

  const setReadAloudPlaying = (isPlaying: boolean) => {
    const changed = readAloudPlaying !== isPlaying;
    readAloudPlaying = isPlaying;
    updateReadAloudFooterState();
    updateMediaSession(isPlaying);
    if (changed) {
      deps.onReadAloudPlaybackChange?.(isPlaying);
    }
  };

  const setReadAloudBuffering = (isBuffering: boolean) => {
    readAloudBuffering = isBuffering;
    updateReadAloudFooterState();
  };

  const ttsVoiceChangedHandler = () => {
    ttsVoiceSelected = true;
  };

  const highlightTTSPosition = () => {
    if (ttsPosition === undefined) {
      deps.clearAudioPositionHighlight();
      return;
    }
    deps.highlightAudioPosition(ttsPosition);
  };

  /**
   * If `position` falls outside the current page, trigger page navigation and
   * return true. Otherwise return false. Used both during playback (via
   * `onAudioPositionUpdated`) and eagerly at sentence end — the next fetch
   * must target the correct page, so pagination runs before we call tts.play.
   */
  const handlePageBoundaryIfNeeded = (
    position: number,
    boundaryOptions: {
      allowPreviousPage?: boolean;
      resumeOnRender?: boolean;
    } = {}
  ): boolean => {
    const lastSpanPosition = readerViewStore.lastPosition.value ?? NaN;
    const firstSpanPosition = readerViewStore.firstPosition.value ?? NaN;

    if (!isNaN(lastSpanPosition) && position > lastSpanPosition) {
      if (readAloudButton?.isPlaying && boundaryOptions.resumeOnRender) {
        pendingResumePosition = position;
      }
      if (!pendingPageTurn) {
        pendingPageTurn = true;
        deps.onGotoNextPage(true);
      }
      return true;
    }
    if (
      boundaryOptions.allowPreviousPage !== false &&
      !isNaN(firstSpanPosition) &&
      position < firstSpanPosition - 1
    ) {
      if (readAloudButton?.isPlaying && boundaryOptions.resumeOnRender) {
        pendingResumePosition = position;
      }
      if (!pendingPageTurn) {
        pendingPageTurn = true;
        deps.onGotoPreviousPage(true);
      }
      return true;
    }
    return false;
  };

  const onAudioPositionUpdated = (
    position: number,
    boundaryOptions: { allowPreviousPage?: boolean } = {}
  ) => {
    ttsPosition = position;
    if (handlePageBoundaryIfNeeded(position, boundaryOptions)) return;
    highlightTTSPosition();
  };

  const pauseReadAloud = (resetPosition?: boolean) => {
    setReadAloudPlaying(false);
    ttsPlaybackRunID++;
    pendingPageTurn = false;
    pendingResumePosition = undefined;
    clearTimeout(pendingSkipTimeout);
    pendingSkipTimeout = undefined;
    if (resetPosition) {
      ttsPosition = undefined;
    }
    clearTimeout(readAloudTimeout);

    if (stores.bookModel.book.value.audioFileID != null) {
      audioPlayer.pause();
      return;
    }
    tts.cancel();
  };

  const restartReadAloud = () => {
    ttsPlaybackRunID++;
    pendingPageTurn = false;
    pendingResumePosition = undefined;
    clearTimeout(pendingSkipTimeout);
    pendingSkipTimeout = undefined;
    clearTimeout(readAloudTimeout);

    if (stores.bookModel.book.value.audioFileID != null) {
      audioPlayer.pause();
    } else {
      tts.cancel();
    }
    setTimeout(playReadAloud, 50);
  };

  const jumpReadAloud = (amount: number) => {
    // A pending skip means we were already mid-skip from a previous click;
    // treat that as "still playing" so chained clicks keep playback alive.
    const wasPlaying = readAloudPlaying || pendingSkipTimeout !== undefined;

    // Clear any deferred resume from a prior skip so this click takes over.
    clearTimeout(pendingSkipTimeout);
    pendingSkipTimeout = undefined;

    // Cancel current audio + boundary polls immediately so the previous
    // sentence can't keep firing onBoundary into a now-wrong position.
    clearTimeout(readAloudTimeout);
    pendingPageTurn = false;
    pendingResumePosition = undefined;
    if (stores.bookModel.book.value.audioFileID != null) {
      audioPlayer.pause();
    } else {
      ttsPlaybackRunID++;
      tts.cancel();
    }

    ttsPosition = Math.max(0, ttsPosition + amount);

    const firstSpanPosition = readerViewStore.firstPosition.value ?? NaN;

    if (ttsPosition < firstSpanPosition) {
      const rememberTTSPosition = ttsPosition;
      // Ideally we'd want to continue playing here, but we first need to fetch the previous page's
      // contents and render it so simpler just to stay paused in this case
      deps.onGotoPreviousPage(true);
      ttsPosition = rememberTTSPosition;
      highlightTTSPosition();
      setReadAloudPlaying(false);
      return;
    }

    highlightTTSPosition();
    if (wasPlaying) {
      // Keep the play icon visible across the debounce so the user doesn't
      // see a flicker between skip clicks.
      setReadAloudPlaying(true);
      pendingSkipTimeout = setTimeout(() => {
        pendingSkipTimeout = undefined;
        playReadAloud();
      }, SKIP_DEBOUNCE_MS);
    } else {
      setReadAloudPlaying(false);
    }
  };

  /**
   * Minimum pause between sentences. Also the window during which the next
   * sentence's Azure audio is fetched in parallel, so the perceived gap is
   * `max(300, fetchLatency)` rather than `300 + fetchLatency`.
   */
  const INTER_SENTENCE_PAUSE_MS = 300;

  const startPlayingFrom = (
    position: number,
    playOptions: { minStartDelayMs?: number } = {}
  ) => {
    if (!skipAudioButtonsShowing) {
      skipAudioButtonsShowing = true;
      updateReadAloudFooterState();
    }

    if (position >= stores.bookModel.book.value.totalWords) {
      // Finished text
      pauseReadAloud();
      deps.clearAudioPositionHighlight();
      return;
    }

    if (stores.bookModel.book.value.audioFileID != null) {
      const playbackRate = parseFloat(
        browserTTSVoices.getRate(stores.bookModel.book.value.language)
      );
      audioPlayer.play(position, playbackRate);
      return;
    }

    const contextSentenceNodes = deps.getSentenceFromPosition(position);

    // Punctuation left over after a sentence break (`... Bruno? ».`) forms a
    // sentence with nothing to pronounce. Step over it rather than asking a
    // provider to speak it — Azure returns an empty clip whose playback error
    // would halt Read Aloud with the punctuation stuck highlighted (forum
    // thread 5254), and some browser voices never report such an utterance as
    // ended. An empty node list means the position isn't on this page at all,
    // which the normal path already handles.
    if (
      contextSentenceNodes.length > 0 &&
      !hasSpeakableText(contextSentenceNodes)
    ) {
      const lastNodeWithRange = contextSentenceNodes
        .slice()
        .reverse()
        .find((node) => node.range);
      const nextPosition = (lastNodeWithRange?.range?.to ?? position) + 1;
      if (
        !handlePageBoundaryIfNeeded(nextPosition, {
          allowPreviousPage: false,
          resumeOnRender: true,
        })
      ) {
        startPlayingFrom(nextPosition, playOptions);
      }
      return;
    }

    const language = stores.bookModel.book.value.language;

    const playTTS = () => {
      let nodesToSpeak = contextSentenceNodes as any[];
      const supportsBoundaries = tts.getVoiceBoundarySupport({
        user: stores.user,
        feature: "readAloud",
        language,
      });

      // Some browser voices do not fire word-by-word boundary events, so the
      // reader cannot detect that a still-speaking sentence has crossed a page
      // boundary. Once a voice is known to lack boundaries, speak only up to
      // the current page end; the normal onEnd page-turn path then resumes on
      // the next page from the following word.
      if (supportsBoundaries === false) {
        const lastSpanPosition = readerViewStore.lastPosition.value ?? NaN;
        if (!isNaN(lastSpanPosition)) {
          let cutIndex = nodesToSpeak.length;
          for (let index = 0; index < nodesToSpeak.length; index++) {
            const node = nodesToSpeak[index];
            if (node.range && node.range.from > lastSpanPosition) {
              cutIndex = index;
              break;
            }
          }
          if (cutIndex < nodesToSpeak.length && cutIndex > 0) {
            nodesToSpeak = nodesToSpeak.slice(0, cutIndex);
          }
        }
      }

      const textParts = nodesToSpeak.map((node) => ({
        text: node.text ?? "",
        position: node.range?.to,
      }));

      const runID = ++ttsPlaybackRunID;
      tts.play({
        user: stores.user,
        feature: "readAloud",
        language,
        text: textParts,
        minStartDelayMs: playOptions.minStartDelayMs,
        // Move the highlight to the first word of the new sentence at the
        // moment playback actually starts — so the last word of the previous
        // sentence stays lit through the pause, matching what's being spoken.
        onStart: () => {
          if (runID !== ttsPlaybackRunID) return;
          ttsPosition = position;
          highlightTTSPosition();
        },
        onBoundary: (boundaryPosition) => {
          if (runID !== ttsPlaybackRunID) return;
          onAudioPositionUpdated(boundaryPosition, {
            allowPreviousPage: false,
          });
        },
        onEnd: (nextPosition) => {
          if (runID !== ttsPlaybackRunID) return;
          clearTimeout(readAloudTimeout);
          if (nextPosition === undefined) return;
          // Eagerly paginate if the next sentence is on a new page — the
          // fetch below would otherwise grab stale content from the old page.
          if (
            handlePageBoundaryIfNeeded(nextPosition, {
              allowPreviousPage: false,
              resumeOnRender: true,
            })
          ) {
            return;
          }
          // Kick off the next fetch immediately; the provider holds playback
          // until at least INTER_SENTENCE_PAUSE_MS has elapsed.
          startPlayingFrom(nextPosition, {
            minStartDelayMs: INTER_SENTENCE_PAUSE_MS,
          });
        },
        onError: () => {
          if (runID !== ttsPlaybackRunID) return;
          pauseReadAloud();
        },
      });
    };

    // The button also shows when no voice is usable yet but upgrading would
    // unlock high quality voices (see addReadAloudButtonIfAppropriate). Only
    // play once a voice is usable at the user's tier.
    const isPlayable = () =>
      tts.getAvailabilitySignal({
        user: stores.user,
        feature: "readAloud",
        language,
      }).value === "supported";

    // A voice is "selected" if voiceStore has one for (readAloud, language) —
    // either a browser voice from localStorage or a server-synced Azure voice.
    // Without this check we'd re-open the picker between every sentence when
    // the user picked Azure (which doesn't touch browserTTSVoices).
    const hasVoice =
      isPlayable() &&
      (ttsVoiceSelected ||
        tts.getVoicePickerState({
          user: stores.user,
          feature: "readAloud",
          language,
        }).selectedVoiceSource === "stored");

    if (hasVoice) {
      ttsVoiceSelected = true;
      playTTS();
      return;
    }

    let willPlay = false;
    modalController.show(
      preact.h(TTSVoicePickerView, {
        language,
        feature: "readAloud",
        close: () => {
          willPlay = isPlayable();
          if (willPlay) ttsVoiceSelected = true;
          modalController.cancelAll();
          if (willPlay) playTTS();
        },
      }),
      {
        // Leaving the picker without playing (dismissed, or still no voice
        // usable at the user's tier on this device) returns the button to
        // paused.
        cancelCallback: () => {
          if (!willPlay) pauseReadAloud();
        },
      }
    );
  };

  const playReadAloud = () => {
    walkthrough.setAchievement("readAloud", true);
    setReadAloudPlaying(true);
    const firstSpanPosition = readerViewStore.firstPosition.value ?? 0;

    startPlayingFrom(
      ttsPosition !== undefined /*&& ttsPosition < lastSpanPosition*/
        ? ttsPosition
        : firstSpanPosition
    );
  };

  const jumpForward = () => {
    // Jump forward 10 seconds
    if (stores.bookModel.book.value.audioFileID) {
      audioPlayer.jump(10);
    } else {
      jumpReadAloud(8);
    }
  };

  const jumpBackward = () => {
    // Jump backwards 10 seconds
    if (stores.bookModel.book.value.audioFileID) {
      audioPlayer.jump(-10);
    } else {
      jumpReadAloud(-8);
    }
  };

  /**
   * Device browser voices can finish loading *after*
   * addReadAloudButtonIfAppropriate has already run and bailed as
   * "unsupported" — Chrome/Edge (notably Android PWAs) enumerate voices
   * asynchronously, sometimes seconds later. When they arrive tts flips the
   * Read Aloud availability signal to "supported"; watch for that and rebuild
   * the footer control so the user doesn't have to close and reopen the text
   * (the workaround reported in forum thread 5103).
   */
  const watchForReadAloudRecovery = (language: string) => {
    if (disposeAvailabilityRecovery) return;
    const availabilitySignal = tts.getAvailabilitySignal({
      user: stores.user,
      feature: "readAloud",
      language,
    });
    disposeAvailabilityRecovery = effect(() => {
      if (availabilitySignal.value !== "supported") return;
      // Button already present — nothing to recover. (Read as a plain variable,
      // not a signal, so re-adding it never re-triggers this effect.)
      if (readAloudButton) return;
      // untracked so addReadAloudButtonIfAppropriate's own signal reads don't
      // become dependencies of this effect (which would re-run it on every
      // page turn).
      void untracked(() => addReadAloudButtonIfAppropriate());
    });
  };

  const addReadAloudButtonIfAppropriate = async () => {
    ttsPosition = undefined;
    readAloudButton = undefined;
    readAloudPlaying = false;
    readAloudBuffering = false;
    skipAudioButtonsShowing = false;
    updateReadAloudFooterState();

    if (stores.bookModel.book.value.youTubeID != null) {
      return;
    }

    // Install Read Aloud whenever playback is reachable: an attached audio
    // file, browser voices on this device, or Azure (Plus/freeForever in a
    // language we have high quality voices for). Gating on browser voices
    // alone hides Read Aloud from paid users on devices/languages with no
    // installed browser voices, even though Azure would happily speak.
    // Also install it when upgrading would make it playable, so pressing play
    // opens the voice picker with its high quality voices upsell (forum 5312).
    const language = stores.bookModel.book.value.language;
    await tts.initialize(language);
    const { selectedVoiceSource, upsellVoiceCount } = tts.getVoicePickerState({
      user: stores.user,
      feature: "readAloud",
      language,
    });
    ttsVoiceSelected = selectedVoiceSource === "stored";

    if (
      stores.bookModel.book.value.audioFileID == null &&
      tts.getAvailabilitySignal({
        user: stores.user,
        feature: "readAloud",
        language,
      }).value !== "supported" &&
      upsellVoiceCount === 0
    ) {
      // No browser/Azure voice yet. Keep watching: device voices may load later
      // (Chrome/Edge Android PWAs) and flip availability to "supported", at
      // which point we rebuild the button instead of requiring a reload.
      watchForReadAloudRecovery(language);
      return;
    }

    await deps.setupFormattingOptions();

    // Some reader modules still ask for a "readAloudButton" object to check
    // playback state. Keep this small facade while the footer itself is owned
    // by Preact state instead of a nested imperative subview.
    readAloudButton = {
      get isPlaying() {
        return readAloudPlaying;
      },
      toggle: () => {
        if (readAloudPlaying) {
          pauseReadAloud();
        } else {
          playReadAloud();
        }
      },
      setIsPlaying: setReadAloudPlaying,
      setIsBuffering: setReadAloudBuffering,
    };
    deps.updateFooterState({
      readAloudAvailable: true,
      readAloudVisible: !options.endOfText,
    });
    updateReadAloudFooterState();

    if (
      stores.bookModel.book.value.audioFileID != null &&
      parseInt(options.from ?? 0, 10) === 0
    ) {
      setTimeout(() => {
        const tooltipElement =
          document.querySelector<HTMLElement>(".playAudioTooltip");
        if (!tooltipElement) {
          return;
        }

        modalController.showLegacy(tooltipElement, {
          center: false,
          closeButton: false,
          title: "Navigate to...",
          cancelCallback: () => {
            // Note: showingPageNavigationTooltip is managed in readerPage
          },
        });
      }, 0);
    }

    if (readAloudButton) {
      keyboardShortcuts.registerHandler(
        "readerPage",
        "l".charCodeAt(0),
        jumpForward
      );
      keyboardShortcuts.registerHandler(
        "readerPage",
        "L".charCodeAt(0),
        jumpForward
      );
      keyboardShortcuts.registerHandler(
        "readerPage",
        keyboardShortcuts.keyCodes.down,
        jumpForward
      );

      keyboardShortcuts.registerHandler(
        "readerPage",
        "j".charCodeAt(0),
        jumpBackward
      );
      keyboardShortcuts.registerHandler(
        "readerPage",
        "J".charCodeAt(0),
        jumpBackward
      );
      keyboardShortcuts.registerHandler(
        "readerPage",
        keyboardShortcuts.keyCodes.up,
        jumpBackward
      );

      const playPauseHandler = (event) => {
        if (stores.bookModel.book.value.youTubeID != null) {
          return;
        }

        if (event.target.id === "readAloudButton" && event.key === " ") {
          // Don't toggle read aloud if the user pressed space while the button was focused, since
          // it will result in cancelling the toggle
          return;
        }

        readAloudButton.toggle();
      };
      keyboardShortcuts.registerHandler(
        "readerPage",
        keyboardShortcuts.keyCodes.space,
        playPauseHandler
      );
      keyboardShortcuts.registerHandler(
        "readerPage",
        "k".charCodeAt(0),
        playPauseHandler
      );
      keyboardShortcuts.registerHandler(
        "readerPage",
        "K".charCodeAt(0),
        playPauseHandler
      );
    }
  };

  /**
   * Handles a freshly-rendered page after Read Aloud auto-pagination. If the
   * current sentence is still playing, this only marks the page turn as
   * complete and lets the audio continue. If the sentence already ended while
   * the page was rendering, this resumes from the queued next-sentence
   * position.
   */
  const resumeIfPending = () => {
    if (!pendingPageTurn && pendingResumePosition === undefined) return;
    pendingPageTurn = false;
    const resumePosition = pendingResumePosition;
    pendingResumePosition = undefined;
    if (!readAloudButton?.isPlaying) return;
    if (resumePosition === undefined) return;
    ttsPosition = resumePosition;
    startPlayingFrom(resumePosition);
  };

  const dispose = () => {
    disposeAvailabilityRecovery?.();
    disposeAvailabilityRecovery = undefined;
    if (navigator.mediaSession) {
      navigator.mediaSession.metadata = null;
      for (const action of [
        "play",
        "pause",
        "nexttrack",
        "previoustrack",
        "seekforward",
        "seekbackward",
      ] as const) {
        navigator.mediaSession.setActionHandler(action, null);
      }
      navigator.mediaSession.setPositionState();
    }
  };

  return {
    addReadAloudButtonIfAppropriate,
    dispose,
    pauseReadAloud,
    restartReadAloud,
    playReadAloud,
    resumeIfPending,
    jumpForward,
    jumpBackward,
    onAudioPositionUpdated,
    ttsVoiceChangedHandler,
    getTTSPosition: () => ttsPosition,
    setTTSPosition: (position: number | undefined) => {
      ttsPosition = position;
    },
    getReadAloudButton: () => readAloudButton,
    highlightTTSPosition,
  };
}

export type ReaderPageReadAloudController = ReturnType<
  typeof createReadAloudController
>;
